import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation, Trans } from 'react-i18next';

import Iconify from 'src/components/Iconify';
import v2_actionMetaListaPaginas from 'src/actions/v2/meta/v2_actionMetaListaPaginas';
import v2_actionMetaVincularPagina from 'src/actions/v2/meta/v2_actionMetaVincularPagina';
import v2_actionDetalhesCanal from 'src/actions/v2/cliente/canais/v2_actionDetalhesCanal';
import { ButtonLoading } from 'src/@/components/use/ButtonLoading';
import PrivacyPolicySection from './PrivacyPolicy';
import { CANAIS_META_PAGE_LIST_DESIGN as DESIGN } from './CanaisMetaPageList.design';

// ----------------------------------------------------------------------

const SkeletonRow = () => (
    <div className={DESIGN.skeletonRow}>
        <div className="w-11 h-11 rounded-xl bg-gray-100 animate-pulse shrink-0" />
        <div className="flex-1 flex flex-col gap-1.5">
            <div className="h-3.5 w-2/5 bg-gray-100 rounded animate-pulse" />
            <div className="h-3 w-1/4 bg-gray-50 rounded animate-pulse" />
        </div>
    </div>
);

const SelectableItem = ({ selected, disabled, onClick, avatarUrl, fallbackIcon, title, subtitle }) => (
    <div
        role="button"
        tabIndex={0}
        onClick={disabled ? undefined : onClick}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick(); }}
        className={`${DESIGN.item.base} ${disabled ? DESIGN.item.disabledLook : DESIGN.item.selectable} ${selected ? DESIGN.item.selected : DESIGN.item.unselected}`}
    >
        <div className={DESIGN.item.avatarWrap}>
            {avatarUrl ? (
                <img src={avatarUrl} alt={title} className={DESIGN.item.avatarImg} />
            ) : (
                <Iconify icon={fallbackIcon} width={22} height={22} className="text-gray-400" />
            )}
        </div>
        <div className="flex-1 min-w-0">
            <p className={DESIGN.item.name}>{title}</p>
            {subtitle && <p className={DESIGN.item.meta}>{subtitle}</p>}
        </div>
        <Iconify
            icon={selected ? 'mdi:check-circle' : 'mdi:checkbox-blank-circle-outline'}
            width={22}
            height={22}
            className={selected ? DESIGN.item.checkOn : DESIGN.item.checkOff}
        />
    </div>
);

export default function CanaisMetaPageList() {
    const { t } = useTranslation('settings');
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const channel = params.get('channel');

    const [canalNome, setCanalNome] = useState('');

    const [listPagesMeta, setListPagesMeta] = useState([]);
    const [loadingPages, setLoadingPages] = useState(true);
    const [pagesError, setPagesError] = useState('');
    // Diagnóstico devolvido pelo backend quando a lista vem vazia (permissão recusada
    // vs. conta sem páginas) — evita o empty state genérico que não diz o que fazer.
    const [pagesDiagnostico, setPagesDiagnostico] = useState(null);

    const [selectedIndex, setSelectedIndex] = useState(null);

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [successOpen, setSuccessOpen] = useState(false);

    const [openPrivacyPolicy, setOpenPrivacyPolicy] = useState(false);

    async function buscarListaPaginasMeta() {
        setLoadingPages(true);
        setPagesError('');
        try {
            const [pagesRes, canalRes] = await Promise.all([
                v2_actionMetaListaPaginas(`?canal_id=${channel}`),
                v2_actionDetalhesCanal(channel).catch(() => null),
            ]);
            setListPagesMeta(pagesRes.data.data || []);
            setPagesDiagnostico(pagesRes.data.diagnostico || null);
            if (canalRes?.data?.data?.nome) setCanalNome(canalRes.data.data.nome);
        } catch (error) {
            const mensagem = error?.response?.data?.mensagem || t('channels.metaPageList.loadPagesError', 'Não foi possível carregar as páginas do Facebook. Tente novamente.');
            setPagesError(mensagem);
            console.error('[CanaisMetaPageList] erro ao buscar páginas Meta:', error?.response?.data || error.message);
        } finally {
            setLoadingPages(false);
        }
    }

    useEffect(() => {
        if (!channel) {
            setLoadingPages(false);
            setPagesError(t('channels.metaPageList.invalidLinkError', 'Link inválido: nenhum canal foi especificado. Volte para a lista de canais e tente novamente.'));
            return;
        }
        buscarListaPaginasMeta();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleSelectPage(index) {
        setSelectedIndex(index);
        setSaveError('');
    }

    async function handleSalvar() {
        if (selectedIndex === null || saving) return;
        const selectedPage = listPagesMeta[selectedIndex];
        setSaving(true);
        setSaveError('');
        try {
            // A vinculação acontece no servidor: é lá que o access token DA página escolhida
            // é resolvido (o callback de OAuth grava sempre o da primeira página) e que os
            // webhooks são assinados.
            const response = await v2_actionMetaVincularPagina(channel, { pageId: selectedPage.id });

            const warnings = response.data?.data?.warnings || [];
            if (warnings.length) {
                enqueueSnackbar(t('channels.metaPageList.webhookWarning', 'Canal vinculado, mas a assinatura do webhook falhou: {{warnings}}', { warnings: warnings.join(' | ') }), {
                    variant: 'warning',
                });
            }

            setSuccessOpen(true);
        } catch (error) {
            const mensagem = error?.response?.data?.mensagem || t('channels.metaPageList.savePageError', 'Erro ao salvar a página selecionada. Tente novamente.');
            setSaveError(mensagem);
            enqueueSnackbar(mensagem, { variant: 'error' });
            console.error('[CanaisMetaPageList] erro ao salvar canal:', error?.response?.data || error.message);
        } finally {
            setSaving(false);
        }
    }

    const selectedPage = selectedIndex !== null ? listPagesMeta[selectedIndex] : null;

    return (
        <>
            <div className={DESIGN.page.container}>
                <div className={DESIGN.page.header.container}>
                    <div className={DESIGN.page.header.iconBox}>
                        <Iconify icon="ic:baseline-facebook" width={24} height={24} />
                    </div>
                    <div>
                        <h1 className={DESIGN.page.header.title}>{t('channels.metaPageList.pageTitle', 'Conectar página do Facebook')}</h1>
                        <p className={DESIGN.page.header.subtitle}>
                            {canalNome
                                ? (
                                    <Trans
                                        t={t}
                                        i18nKey="channels.metaPageList.subtitleWithChannel"
                                        defaults="Selecione a página que deseja conectar ao canal <strong1>{{canalNome}}</strong1>."
                                        components={{ strong1: <strong /> }}
                                        values={{ canalNome }}
                                    />
                                )
                                : t('channels.metaPageList.subtitleGeneric', 'Selecione a página do Facebook que deseja conectar a este canal.')}
                        </p>
                    </div>
                </div>

                <div className={DESIGN.page.content}>
                    <div className="flex flex-col gap-8">
                        {/* Step 1 — Páginas */}
                        <div className={DESIGN.step.container}>
                            <div className={DESIGN.step.header}>
                                <span className={`${DESIGN.step.badge} ${selectedPage ? DESIGN.step.badgeDone : DESIGN.step.badgeActive}`}>
                                    {selectedPage ? <Iconify icon="mdi:check" width={14} height={14} /> : '1'}
                                </span>
                                <span className={DESIGN.step.title}>{t('channels.metaPageList.stepTitle', 'Escolha a página do Facebook')}</span>
                            </div>
                            <p className={DESIGN.step.subtitle}>{t('channels.metaPageList.stepSubtitle', 'As páginas listadas abaixo vieram da sua conta do Facebook conectada.')}</p>

                            {loadingPages ? (
                                <div className="flex flex-col gap-2">
                                    {[...Array(3)].map((_, i) => <SkeletonRow key={i} />)}
                                </div>
                            ) : pagesError ? (
                                <div className={DESIGN.error}>
                                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-500">
                                        <Iconify icon="solar:danger-circle-bold" width={24} height={24} />
                                    </div>
                                    <p className="text-sm text-gray-700 font-medium">{pagesError}</p>
                                    {channel && (
                                        <button
                                            onClick={buscarListaPaginasMeta}
                                            className="mt-1 bg-white text-red-600 px-4 py-2 rounded-xl border border-red-200 text-xs font-semibold hover:bg-red-50 transition-all shadow-sm flex items-center gap-2"
                                        >
                                            <Iconify icon="solar:restart-bold" width={16} height={16} />
                                            {t('channels.metaPageList.retryButton', 'Tentar novamente')}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => navigate('/dashboard/configuracoes/canais')}
                                        className="text-xs font-medium text-gray-500 hover:text-gray-700 underline"
                                    >
                                        {t('channels.metaPageList.backToChannelsButton', 'Voltar para canais')}
                                    </button>
                                </div>
                            ) : listPagesMeta.length === 0 ? (
                                <div className={DESIGN.empty}>
                                    <div className="w-12 h-12 rounded-2xl bg-white border border-gray-100 flex items-center justify-center mb-3 mx-auto">
                                        <Iconify icon="mdi:facebook" width={24} height={24} className="text-gray-400" />
                                    </div>
                                    <p className="text-sm font-medium text-gray-700">{t('channels.metaPageList.emptyTitle', 'Nenhuma página encontrada')}</p>
                                    <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                                        {pagesDiagnostico?.motivo
                                            || t('channels.metaPageList.emptyDefaultReason', 'Verifique se você é administrador de alguma página do Facebook com essa conta e tente novamente.')}
                                    </p>

                                    {pagesDiagnostico && (
                                        <div className="mt-4 mx-auto max-w-sm text-left bg-white border border-gray-100 rounded-xl p-3">
                                            {pagesDiagnostico.usuario && (
                                                <p className="text-[11px] text-gray-500">
                                                    <Trans
                                                        t={t}
                                                        i18nKey="channels.metaPageList.connectedAccountLabel"
                                                        defaults="Conta conectada: <span1>{{usuario}}</span1>"
                                                        components={{ span1: <span className="font-semibold text-gray-700" /> }}
                                                        values={{ usuario: pagesDiagnostico.usuario }}
                                                    />
                                                </p>
                                            )}
                                            {pagesDiagnostico.faltando?.length > 0 && (
                                                <p className="text-[11px] text-red-600 mt-1">
                                                    <Trans
                                                        t={t}
                                                        i18nKey="channels.metaPageList.missingPermissionsLabel"
                                                        defaults="Permissões não concedidas: <span1>{{permissoes}}</span1>"
                                                        components={{ span1: <span className="font-mono" /> }}
                                                        values={{ permissoes: pagesDiagnostico.faltando.join(', ') }}
                                                    />
                                                </p>
                                            )}
                                            {pagesDiagnostico.concedidas?.length > 0 && (
                                                <p className="text-[11px] text-gray-400 mt-1 break-words">
                                                    <Trans
                                                        t={t}
                                                        i18nKey="channels.metaPageList.grantedPermissionsLabel"
                                                        defaults="Concedidas: <span1>{{permissoes}}</span1>"
                                                        components={{ span1: <span className="font-mono" /> }}
                                                        values={{ permissoes: pagesDiagnostico.concedidas.join(', ') }}
                                                    />
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    <button
                                        onClick={() => navigate('/dashboard/configuracoes/canais')}
                                        className="mt-4 text-xs font-medium text-[#7C3AED] hover:underline"
                                    >
                                        {t('channels.metaPageList.backAndReconnectButton', 'Voltar e conectar novamente')}
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto pr-1">
                                    {listPagesMeta.map((item, index) => (
                                        <SelectableItem
                                            key={item.id}
                                            selected={selectedIndex === index}
                                            onClick={() => handleSelectPage(index)}
                                            avatarUrl={item.profile}
                                            fallbackIcon="mdi:facebook"
                                            title={item.name}
                                            subtitle={item.category || item.id}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Footer / ação */}
                        <div className={DESIGN.footer.container}>
                            {saveError && (
                                <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
                                    <Iconify icon="solar:danger-circle-bold" width={16} height={16} className="shrink-0" />
                                    {saveError}
                                </div>
                            )}

                            <div className={DESIGN.footer.consent}>
                                <span>{t('channels.metaPageList.consentPrefix', 'Ao prosseguir, você concorda com nossa')}</span>
                                <button className={DESIGN.footer.link} onClick={() => setOpenPrivacyPolicy(true)}>
                                    {t('channels.metaPageList.privacyPolicyLink', 'política de privacidade')}
                                </button>
                                <span>.</span>
                            </div>

                            <ButtonLoading
                                onClick={handleSalvar}
                                loading={saving}
                                disabled={selectedIndex === null}
                                className={DESIGN.footer.primaryButton}
                            >
                                {!saving && <Iconify icon="mdi:check-circle-outline" width={18} height={18} className="mr-1.5" />}
                                {t('channels.metaPageList.saveButton', 'Salvar e conectar')}
                            </ButtonLoading>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal política de privacidade */}
            {openPrivacyPolicy && (
                <div className={DESIGN.modal.backdrop} onClick={() => setOpenPrivacyPolicy(false)}>
                    <div className={DESIGN.modal.card} onClick={(e) => e.stopPropagation()}>
                        <div className={DESIGN.modal.header}>
                            <h3 className={DESIGN.modal.title}>{t('channels.metaPageList.privacyModalTitle', 'Política de Privacidade da Plataforma')}</h3>
                            <button
                                onClick={() => setOpenPrivacyPolicy(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                            >
                                <Iconify icon="eva:close-outline" width={20} height={20} />
                            </button>
                        </div>
                        <div className={DESIGN.modal.body}>
                            <PrivacyPolicySection />
                        </div>
                        <div className={DESIGN.modal.footer}>
                            <button
                                onClick={() => setOpenPrivacyPolicy(false)}
                                className="px-4 py-2 bg-[#7C3AED] hover:bg-[#6d28d9] text-white text-sm font-medium rounded-xl transition-all"
                            >
                                {t('channels.metaPageList.privacyModalCloseButton', 'Ok, entendi')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sucesso */}
            {successOpen && (
                <div className={DESIGN.successOverlay.backdrop}>
                    <div className={DESIGN.successOverlay.card}>
                        <div className={DESIGN.successOverlay.iconBox}>
                            <Iconify icon="mdi:check-circle" width={28} height={28} />
                        </div>
                        <h3 className={DESIGN.successOverlay.title}>{t('channels.metaPageList.successTitle', 'Canal configurado com sucesso!')}</h3>
                        <p className={DESIGN.successOverlay.message}>
                            {t('channels.metaPageList.successMessage', 'A página do Facebook foi vinculada a este canal.')}
                        </p>
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => navigate('/dashboard/configuracoes/canais')}
                                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                            >
                                {t('channels.metaPageList.backToChannelsButton', 'Voltar para canais')}
                            </button>
                            <button
                                onClick={() => navigate('/chats/chat')}
                                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-[#7C3AED] hover:bg-[#6d28d9] rounded-xl transition-colors shadow-sm"
                            >
                                {t('channels.metaPageList.goToChatButton', 'Ir para o chat')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
